import { useEffect, useState } from "react";
import { DepartureYearButton } from "./DepartureYearButton";
import styles from "./DepartureYearToggle.module.scss";
import { gtmSendEvent } from "@wojo/analytics";
import { useTourPageContext } from "../../../_client/context";
import { useI18n } from "@wojo/localization";

type DepartureYearToggleProps = {
    listRef: React.RefObject<HTMLDivElement>;
    headerHeight: number;
    years: string[];
};

export const DepartureYearToggle: React.FC<DepartureYearToggleProps> = ({
    listRef,
    headerHeight,
    years,
}) => {
    const { tourContent } = useTourPageContext();
    const { productPage } = useI18n("productPage");

    const [yearVisibility, setYearVisibility] = useState<
        Record<string, boolean | undefined>
    >(() => {
        return years.reduce<Record<string, boolean>>((acc, year) => {
            acc[year] = false;
            return acc;
        }, {});
    });
    const yearInView = Object.keys(yearVisibility)
        .sort()
        .find((year) => yearVisibility[year]);

    /* 
        Since multiple years can be in view simultaneously, this tracks the states of every year row 
        in a key value pair. The year is the key and the value is whether or not it's visible. The object
        gets updated when the intersection observer detects a change in visibility.
    */
    useEffect(() => {
        const intersectionObserver = new IntersectionObserver(
            (entries) => {
                setYearVisibility((yearVisibility) =>
                    entries.reduce(
                        (acc, entry) => {
                            if (entry.target instanceof HTMLElement) {
                                const year = entry.target.dataset.year;
                                if (year && typeof acc[year] === "boolean") {
                                    acc[year] = entry.isIntersecting;
                                }
                            }
                            return acc;
                        },
                        { ...yearVisibility },
                    ),
                );
            },
            {
                root: listRef.current,
                rootMargin: `-${headerHeight * 2}px 0px 0px`,
                threshold: [0, 0.25, 0.75, 1],
            },
        );

        listRef.current?.querySelectorAll("[data-year]").forEach((year) => {
            intersectionObserver.observe(year);
        });
        return () => {
            intersectionObserver.disconnect();
        };
    }, [years, headerHeight, listRef]);

    if (years.length < 2) {
        return null;
    }

    const handleClick = (year: string) => {
        const departureList = listRef.current;
        const yearElement = departureList?.querySelector<HTMLElement>(
            `[data-year="${year}"]`,
        );
        if (departureList && yearElement) {
            departureList.scrollTo({
                top: yearElement.offsetTop - headerHeight,
                behavior: matchMedia("(prefers-reduced-motion: reduce)").matches
                    ? "instant"
                    : "smooth",
            });
            const firstRow =
                yearElement.querySelector<HTMLElement>("tr:first-child");
            if (firstRow) {
                firstRow.focus({ preventScroll: true });
            }
        }
        gtmSendEvent({
            event: "trip_builder_year_selected",
            ga4Tracking: {
                ga4_object: "TRIP_BUILDER",
                ga4_action: "YEAR_SELECTED",
                year: year,
                tourName: tourContent.name ?? "",
                tourCode: tourContent.tourCode ?? "",
            },
        });
    };

    return (
        <nav
            className={styles.nav}
            aria-label={productPage.tripBuilder.yearSwitcher.ariaLabel.l()}
        >
            <div className={styles["year-toggle-wrapper"]}>
                {years.map((year) => {
                    return (
                        <DepartureYearButton
                            key={year}
                            year={year}
                            onClick={handleClick}
                            isActive={(year) => {
                                return yearInView === year;
                            }}
                        />
                    );
                })}
            </div>
        </nav>
    );
};
